import type {
  FleetMapHostMessage,
  FleetMapInit,
  FleetMapStop,
  FleetMapTrail,
  FleetMapVehicle,
} from '@bonbon/types';

export interface MapState {
  /** Null until the host has said who it is and how to draw; nothing loads before. */
  init: FleetMapInit | null;
  vehicles: FleetMapVehicle[];
  stops: FleetMapStop[];
  /** One trail at a time, and only ever the selected van's path. */
  trail: FleetMapTrail | null;
  selectedId: string | null;
  /** A van and a stop are never both selected. */
  selectedStopId: string | null;
}

export const INITIAL_MAP_STATE: MapState = {
  init: null,
  vehicles: [],
  stops: [],
  trail: null,
  selectedId: null,
  selectedStopId: null,
};

export type MapAction =
  | { kind: 'host'; message: FleetMapHostMessage }
  | { kind: 'select'; telematicsVehicleId: string | null }
  | { kind: 'selectStop'; stopId: string | null };

/**
 * Choosing a van drops the stop selection and the other way round; choosing
 * nothing leaves the other kind alone. The host sends "no van" and a stop
 * together when a stop is picked, in either order, and neither may undo the
 * other.
 */
function select(state: MapState, telematicsVehicleId: string | null): MapState {
  // A trail belongs to the van it was drawn for; selecting another drops it.
  const trail = state.trail?.telematicsVehicleId === telematicsVehicleId ? state.trail : null;
  return {
    ...state,
    selectedId: telematicsVehicleId,
    trail,
    selectedStopId: telematicsVehicleId === null ? state.selectedStopId : null,
  };
}

function selectStop(state: MapState, stopId: string | null): MapState {
  if (stopId === null) return { ...state, selectedStopId: null };
  return { ...state, selectedStopId: stopId, selectedId: null, trail: null };
}

export function reduceMapState(state: MapState, action: MapAction): MapState {
  if (action.kind === 'select') return select(state, action.telematicsVehicleId);
  if (action.kind === 'selectStop') return selectStop(state, action.stopId);

  const { message } = action;
  switch (message.type) {
    case 'init':
      return { ...state, init: message.payload };
    case 'vehicles':
      return { ...state, vehicles: message.payload.vehicles };
    case 'stops':
      return { ...state, stops: message.payload.stops };
    case 'trail':
      // A trail that lands after the selection moved on (a slow fetch for the
      // van selected before) belongs to nothing on screen: drawing it, or
      // letting its "no points" clear the current van's, would be wrong.
      if (message.payload.telematicsVehicleId !== state.selectedId) return state;
      return {
        ...state,
        trail: message.payload.points.length === 0 ? null : message.payload,
      };
    case 'select':
      return select(state, message.payload.telematicsVehicleId);
    case 'selectStop':
      return selectStop(state, message.payload.stopId);
  }
}
